/**
 * Footer — componente customizado do odc (ODC 2027)
 *
 * Semantic UI-free: usa @plone/components (Container).
 * Estrutura baseada no layout: "For more information", "Stay Connected"
 * (CTAs), logos de parceiros e copyright. O botão "voltar ao topo" é
 * flutuante e vive fora do footer — ver `components/BackToTop/BackToTop.tsx`.
 *
 * Padrão de shadowing (2 arquivos):
 *   customizations/volto/.../Footer.jsx → re-exporta deste arquivo.
 *
 * Observações:
 * - Textos são internacionalizáveis (base EN, tradução em pt_BR).
 * - Logos de parceiros: ver o bloco PARTNERS abaixo — quais marcas, em que
 *   ordem e o histórico das trocas estão explicados lá.
 * - "Subscribe for Updates" aponta para o formulário externo da UNESCO
 *   (SUBSCRIBE_URL) — mesmo destino do CTA do Hero e do painel "Get Involved".
 * - Os links de "follow" vêm do painel de controle (Social Networks); sem
 *   redes cadastradas, só o botão de subscribe é exibido.
 */
import { defineMessages, useIntl } from 'react-intl';
import { Container } from '@plone/components';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import {
  SocialNetworksProvider,
  useSocialNetworksContext,
} from '../SocialNetworksProvider/SocialNetworksProvider';
import { NETWORK_ICONS } from '../SocialLinks/SocialLinks';

import logoUnescoIoc from '../../assets/logos/logo-unesco-ioc.svg';
import logoMcti from '../../assets/logos/logo-mcti.png';
import logoDecadaCienciaOceanicaBr from '../../assets/logos/logo-decada-ciencia-oceanica-br.png';
// Fora do mockup de 2026-09-30 — ver o bloco PARTNERS abaixo.
// import logoPrefeituraRio from '../../assets/logos/logo-prefeitura-rio.png';
// import logoSerpro from '../../assets/logos/logo-serpro.png';
import bellSVG from '../../icons/bell.svg';

const messages = defineMessages({
  forMoreInformation: {
    id: 'footer.forMoreInformation',
    defaultMessage: 'For more information',
  },
  secretariatLabel: {
    id: 'footer.secretariatLabel',
    defaultMessage: 'Official Conference Secretariat',
  },
  oceanDecadeLabel: {
    id: 'footer.oceanDecadeLabel',
    defaultMessage: 'Ocean Decade',
  },
  stayConnected: {
    id: 'footer.stayConnected',
    defaultMessage: 'Stay Connected',
  },
  stayConnectedText: {
    id: 'footer.stayConnectedText',
    defaultMessage:
      'Stay informed about registration, programme announcements, participation opportunities and Conference updates.',
  },
  subscribe: {
    id: 'footer.subscribe',
    defaultMessage: 'Subscribe for Updates',
  },
  followNetwork: {
    id: 'footer.followNetwork',
    defaultMessage: 'Follow Ocean Decade on {network}',
  },
  partners: {
    id: 'footer.partners',
    defaultMessage: 'Partners',
  },
  copyright: {
    id: 'footer.copyright',
    defaultMessage:
      '© ODS Brasil 2027. Content developed by the Ministry of Foreign Affairs and the Secretariat of Social Communication of the Presidency of the Republic of Brazil.',
  },
});

// Contatos (dados, não strings de UI)
const SECRETARIAT_EMAIL = 'odc2027@mcti.gov.br';
const OCEAN_DECADE_EMAIL = 'event@oceandecade-conference.com';
const SUBSCRIBE_URL = 'https://www.surveymonkey.com/r/OceanDecade27_Updates';

// Logos de parceiros. Mockup do designer de 2026-09-30: três marcas, nesta
// ordem — COI/UNESCO, MCTI, Década da Ciência Oceânica Brasil — numa faixa
// de largura total abaixo das duas colunas. Sem entrada separada para a
// bandeira do Brasil: o logo do MCTI já a traz na própria marca.
//
// Histórico: o 1º logo era o lockup duplo COI/UNESCO + Década do Oceano da
// ONU; o item 9 do feedback da UNESCO (2026-09) pediu só o COI/UNESCO. O
// arquivo antigo segue em assets/logos/logo-unesco-ocean-decade.svg. Entre
// 2026-09-22 e 2026-09-30 o rodapé mostrou só COI/UNESCO + Década Brasil
// (período eleitoral), com as marcas dentro da 1ª coluna.
//
// Prefeitura do Rio e Serpro estavam na ordem pedida pela UNESCO (item 10),
// mas não aparecem no mockup novo. Ficam comentadas até alguém confirmar se
// voltam — se voltarem, é descomentar os imports (acima) e as entradas
// (abaixo); a faixa já quebra linha sozinha. Peça ao designer a altura
// delas antes: sem medida, herdam o fallback de 44px. Ver
// docs/specs/footer.md §6.2.
//
// O `id` vira modificador de classe no CSS: cada marca tem altura própria,
// porque a proporção de cada arquivo é diferente. Ver _footer.scss.
const PARTNERS: { id: string; name: string; logo: string }[] = [
  {
    id: 'unesco-ioc',
    name: 'UNESCO / Intergovernmental Oceanographic Commission',
    logo: logoUnescoIoc,
  },
  {
    id: 'mcti',
    name: 'Ministério da Ciência, Tecnologia e Inovação',
    logo: logoMcti,
  },
  // { id: 'prefeitura-rio', name: 'Prefeitura do Rio', logo: logoPrefeituraRio },
  {
    id: 'decada-br',
    name: 'Década da Ciência Oceânica — Brasil',
    logo: logoDecadaCienciaOceanicaBr,
  },
  // { id: 'serpro', name: 'Serpro', logo: logoSerpro },
];

// Botões de "follow" das redes cadastradas no painel de controle, no estilo
// outlined do mockup (ícone + rótulo). Sem redes cadastradas, não renderiza.
const FooterSocialActions = () => {
  const intl = useIntl();
  const { networks, loaded, error } = useSocialNetworksContext();

  if (!loaded || error || networks.length === 0) {
    return null;
  }

  return (
    <>
      {networks.map((network) => {
        const name = network.social_network;
        if (!name || !network.social_network_url) return null;

        const icon = NETWORK_ICONS[name];
        return (
          <a
            key={network['@id'] || name}
            className="footer__action"
            href={network.social_network_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {icon && <Icon name={icon} size="18px" ariaHidden />}
            {intl.formatMessage(messages.followNetwork, { network: name })}
          </a>
        );
      })}
    </>
  );
};

const Footer = () => {
  const intl = useIntl();

  return (
    <SocialNetworksProvider>
      <footer className="footer" role="contentinfo">
        <Container>
          <div className="footer__top">
            {/* For more information */}
            <section className="footer__info">
              <h2 className="footer__heading">
                {intl.formatMessage(messages.forMoreInformation)}
              </h2>
              <ul className="footer__contacts">
                <li>
                  <span className="footer__contact-label">
                    {intl.formatMessage(messages.secretariatLabel)}
                  </span>
                  <a href={`mailto:${SECRETARIAT_EMAIL}`}>
                    {SECRETARIAT_EMAIL}
                  </a>
                </li>
                <li>
                  <span className="footer__contact-label">
                    {intl.formatMessage(messages.oceanDecadeLabel)}
                  </span>
                  <a href={`mailto:${OCEAN_DECADE_EMAIL}`}>
                    {OCEAN_DECADE_EMAIL}
                  </a>
                </li>
              </ul>
            </section>

            {/* Stay Connected */}
            <section className="footer__connect">
              <h2 className="footer__heading">
                {intl.formatMessage(messages.stayConnected)}
              </h2>
              <p className="footer__text">
                {intl.formatMessage(messages.stayConnectedText)}
              </p>
              <div className="footer__actions">
                <a
                  className="footer__action"
                  href={SUBSCRIBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name={bellSVG} size="18px" ariaHidden />
                  {intl.formatMessage(messages.subscribe)}
                </a>
                <FooterSocialActions />
              </div>
            </section>
          </div>

          <section
            className="footer__partners"
            aria-label={intl.formatMessage(messages.partners)}
          >
            <ul className="footer__partners-list">
              {PARTNERS.map(({ id, name, logo }) => (
                <li
                  className={`footer__partner footer__partner--${id}`}
                  key={id}
                >
                  <img
                    className={`footer__partner-logo footer__partner-logo--${id}`}
                    src={logo}
                    alt={name}
                  />
                </li>
              ))}
            </ul>
          </section>

          <div className="footer__bottom">
            <p className="footer__copyright">
              {intl.formatMessage(messages.copyright)}
            </p>
          </div>
        </Container>
      </footer>
    </SocialNetworksProvider>
  );
};

export default Footer;
